import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useGoogleLocationClient } from '../api/GoogleLocationClientContext'
import {
  GoogleLocation,
  QUERY,
  SaveRequest,
  SelectRequest
} from '../api/GoogleLocationAPI.model'

/**
 * Per restaurant: logging out and into another venue keeps the tab and its
 * cache, and the other venue's place must not show, or be copied into this
 * one's website.
 */
export const googleLocationQueryKey = () => [
  QUERY.GOOGLE_LOCATION,
  localStorage.getItem('restaurantId')
]

/**
 * The venue's connected place, for anything that only reads it.
 *
 * Read once when something asks for it and then from the cache: the app's
 * client neither retries nor refetches on focus (`main.tsx`), so nothing here
 * sends a request the owner did not cause.
 */
export function useGoogleLocationQuery() {
  const client = useGoogleLocationClient()
  return useQuery<GoogleLocation | null>({
    queryKey: googleLocationQueryKey(),
    queryFn: () => client.getLocation()
  })
}

/**
 * The connected place, a pick, and the two ways to change what is kept.
 *
 * A pick only looks the place up: the cache, and so the website and the
 * review link, keep the saved place until `save` or `disconnect` runs.
 */
export function useGoogleLocation() {
  const client = useGoogleLocationClient()
  const queryClient = useQueryClient()
  const location = useGoogleLocationQuery()

  const select = useMutation({
    mutationFn: (request: SelectRequest) => client.select(request)
  })

  const save = useMutation({
    mutationFn: (request: SaveRequest) => client.save(request),
    onSuccess: saved =>
      queryClient.setQueryData(googleLocationQueryKey(), saved)
  })

  const disconnect = useMutation({
    mutationFn: () => client.disconnect(),
    onSuccess: () => queryClient.setQueryData(googleLocationQueryKey(), null)
  })

  return { location, select, save, disconnect }
}
